// src/pages/analytics/salesFilterBar.tsx
//
// Shared customer/item-side filter set used by the Sales Trends and Lost
// Business tabs (mirrors the params the sales-trend + sales-gaps recipes
// accept: salesman, salesman1, category [CUSTOMER category], customer [exact
// code], customerName [substring], driver, item [itemNo or desc substring],
// itemCategory). Dropdown data comes from the analytics-filters recipe.
//
// Controls follow the FilterSelect style from AnalyticsPageV2 so the new tabs
// look native next to the existing Sales/AR detail views.

import { ChevronDown } from 'lucide-react';
import type { AnalyticsFilters } from '../../types';
import { subcategoryOptions } from './itemSubcategories';

export interface SharedSalesFilterState {
  salesman: string;
  salesman1: string;
  category: string;      // CUSTOMER category
  customer: string;      // exact customer code
  customerName: string;  // substring
  driver: string;
  item: string;          // itemNo exact or description substring
  itemCategory: string;
  itemSubcategory: string;   // Invent.Subcat (agent 3.21.0+)
}

export const EMPTY_SALES_FILTERS: SharedSalesFilterState = {
  salesman: '', salesman1: '', category: '', customer: '',
  customerName: '', driver: '', item: '', itemCategory: '', itemSubcategory: '',
};


/** Convert state to recipe params — empty strings become undefined (omitted). */
export function salesFilterParams(s: SharedSalesFilterState): Record<string, string | undefined> {
  return {
    salesman:     s.salesman || undefined,
    salesman1:    s.salesman1 || undefined,
    category:     s.category || undefined,
    customer:     s.customer || undefined,
    customerName: s.customerName || undefined,
    driver:       s.driver || undefined,
    item:         s.item || undefined,
    itemCategory: s.itemCategory || undefined,
    itemSubcategory: s.itemSubcategory || undefined,
  };
}

export function FilterSelect({ label, value, onChange, options, placeholder = 'All' }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)}
          className="appearance-none px-2.5 py-1.5 pr-7 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 cursor-pointer min-w-[120px]">
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      </div>
    </div>
  );
}

export function FilterText({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="px-2.5 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[150px]" />
    </div>
  );
}

export function SalesFilterBar({ value, onChange, filters, showItemFilters = true }: {
  value: SharedSalesFilterState;
  onChange: (next: SharedSalesFilterState) => void;
  filters?: AnalyticsFilters;
  showItemFilters?: boolean;
}) {
  const set = (patch: Partial<SharedSalesFilterState>) => onChange({ ...value, ...patch });
  const subcats = subcategoryOptions(filters, value.itemCategory);
  return (
    <>
      <FilterSelect label="Salesman" value={value.salesman} onChange={v => set({ salesman: v })}
        options={(filters?.salesmen ?? []).map(s => ({ value: s, label: s }))} />
      <FilterSelect label="Salesman 1" value={value.salesman1} onChange={v => set({ salesman1: v })}
        options={(filters?.salesmen1 ?? []).map(s => ({ value: s, label: s }))} />
      <FilterSelect label="Category" value={value.category} onChange={v => set({ category: v })}
        options={(filters?.categories ?? []).map(c => ({ value: c, label: c }))} />
      <FilterSelect label="Route" value={value.driver} onChange={v => set({ driver: v })}
        options={(filters?.drivers ?? []).map(d => ({ value: d, label: d }))} />
      <FilterSelect label="Customer" value={value.customer} onChange={v => set({ customer: v })}
        options={(filters?.customers ?? []).map(c => ({ value: String(c.code), label: `${c.code} - ${c.name}` }))}
        placeholder="All Customers" />
      <FilterText label="Customer Name" value={value.customerName} onChange={v => set({ customerName: v })}
        placeholder="Name contains…" />
      {showItemFilters && (
        <>
          <FilterText label="Item" value={value.item} onChange={v => set({ item: v })}
            placeholder="Item # or description…" />
          <FilterSelect label="Item Category" value={value.itemCategory} onChange={v => set({ itemCategory: v, itemSubcategory: '' })}
            options={(filters?.itemCategories ?? []).map(c => ({ value: c, label: c }))} />
          {subcats && (
            <FilterSelect label="Item Subcategory" value={value.itemSubcategory} onChange={v => set({ itemSubcategory: v })}
              options={subcats.map(c => ({ value: c, label: c }))} />
          )}
        </>
      )}
    </>
  );
}
